import {
  Button,
  ButtonGroup,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { JCheck, Mail, Undo } from '@/client/components/icons/ContinuIcons';

import type { AssignmentActionInput } from '@/client/services/api/graphql/gql/graphql';
import { useAccessHelper } from '@/client/services/hooks/auth/useAccessHelper';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkflowActions } from '../hooks/useWorkflowActions';

interface AssignmentBulkActionsButtonGroupProps {
  payload: AssignmentActionInput;
  refetch: () => void;
  rowSelection: { [key: string]: boolean };
  setRowSelection: (rowSelection: { [key: string]: boolean }) => void;
}

export default function AssignmentBulkActionsButtonGroup({
  payload,
  refetch,
  rowSelection,
  setRowSelection,
}: AssignmentBulkActionsButtonGroupProps) {
  const { t } = useTranslation();
  const { isAllowed } = useAccessHelper();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalType, setModalType] = useState<'markCompleted' | 'sendReminder' | 'unassign' | null>(
    null,
  );

  const {
    markAssignmentsCompletedMutation,
    sendAssignmentsReminderMutation,
    archiveAssignmentsMutation,
  } = useWorkflowActions();

  const permissions = {
    edit: isAllowed(['admin', 'assignments', 'edit'], null, ['admin', 'creator', 'manager']),
    view: isAllowed(['admin', 'assignments', 'view'], null, ['admin', 'creator', 'manager']),
    archive: isAllowed(['admin', 'assignments', 'archive'], null, ['admin', 'creator', 'manager']),
    complete: isAllowed(['admin', 'assignments', 'complete'], null, [
      'admin',
      'creator',
      'manager',
    ]),
  };

  const resetOnSuccess = () => {
    setRowSelection({});

    refetch();
  };

  const handleModalClose = () => {
    setModalType(null);
    onClose();
  };

  const modalDataMap = {
    markCompleted: {
      imagePath: '/public/images/Approval-2--Streamline-Brooklyn.svg',
      title: t('admin.assignments.details.recipients.bulkActions.complete.title', {
        count: Object.keys(rowSelection).length,
      }),
      description: t('admin.assignments.details.recipients.bulkActions.complete.description'),
      submitButtonText: t(
        'admin.assignments.details.recipients.bulkActions.complete.submitButtonText',
      ),
      onSubmit: () => markAssignmentsCompletedMutation.mutateAsync(payload).then(resetOnSuccess),
    },
    sendReminder: {
      imagePath: '/public/images/Receive-Notification-2--Streamline-Brooklyn.svg',
      title: t('admin.assignments.details.recipients.bulkActions.remind.title', {
        count: Object.keys(rowSelection).length,
      }),
      description: t('admin.assignments.details.recipients.bulkActions.remind.description'),
      submitButtonText: t(
        'admin.assignments.details.recipients.bulkActions.remind.submitButtonText',
      ),
      onSubmit: () => sendAssignmentsReminderMutation.mutateAsync(payload).then(resetOnSuccess),
    },
    unassign: {
      imagePath: '/public/images/add-files-E-commerce.svg',
      title: t('admin.assignments.details.recipients.bulkActions.unassign.title', {
        count: Object.keys(rowSelection).length,
      }),
      description: t('admin.assignments.details.recipients.bulkActions.unassign.description'),
      submitButtonText: t(
        'admin.assignments.details.recipients.bulkActions.unassign.submitButtonText',
      ),
      onSubmit: () => archiveAssignmentsMutation.mutateAsync(payload).then(resetOnSuccess),
    },
  };

  return (
    <>
      <ButtonGroup>
        {permissions.complete && (
          <Tooltip
            hasArrow
            label={t('admin.assignments.tableActions.markComplete')}
            placement="top"
          >
            <IconButton
              variant="roundNeutralIconButton"
              aria-label={t('admin.assignments.tableActions.markComplete')}
              icon={<JCheck />}
              onClick={() => {
                setModalType('markCompleted');
                onOpen();
              }}
            />
          </Tooltip>
        )}

        {(permissions.edit || permissions.view) && (
          <Tooltip
            hasArrow
            label={t('admin.assignments.tableActions.sendReminder')}
            placement="top"
          >
            <IconButton
              variant="roundNeutralIconButton"
              aria-label={t('admin.assignments.tableActions.sendReminder')}
              icon={<Mail />}
              onClick={() => {
                setModalType('sendReminder');
                onOpen();
              }}
            />
          </Tooltip>
        )}

        {permissions.archive && (
          <Tooltip hasArrow label={t('admin.assignments.tableActions.unassign')} placement="top">
            <IconButton
              variant="roundNeutralIconButton"
              aria-label={t('admin.assignments.tableActions.unassign')}
              icon={<Undo />}
              onClick={() => {
                setModalType('unassign');
                onOpen();
              }}
            />
          </Tooltip>
        )}
      </ButtonGroup>

      {modalType && (
        <Modal isOpen={isOpen} onClose={handleModalClose} isCentered>
          <ModalOverlay />

          <ModalContent paddingTop={12} paddingX={10} paddingBottom={8}>
            <ModalBody>
              <VStack spacing={4} marginBottom={10} textAlign="center">
                <Image
                  src={modalDataMap[modalType].imagePath}
                  alt={modalDataMap[modalType].title}
                />

                <Text fontSize="20px" fontWeight={600}>
                  {modalDataMap[modalType].title}
                </Text>

                <Text fontSize="14px" fontWeight={500}>
                  {modalDataMap[modalType].description}
                </Text>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <ButtonGroup width="full" justifyContent="center">
                <Button variant="createCancel" onClick={handleModalClose}>
                  {t('global.actions.cancel')}
                </Button>

                <Button onClick={modalDataMap[modalType].onSubmit}>
                  {modalDataMap[modalType].submitButtonText}
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}
